import { useEffect, useRef, useCallback } from 'react';

interface UseIdleTimerOptions {
  /** Total idle time before logout fires (milliseconds) */
  idleTimeout: number;
  /** How long before idleTimeout to show the warning dialog (milliseconds) */
  warningDuration: number;
  /** Called when idle warning should be shown */
  onWarning: () => void;
  /** Called when user has been idle for idleTimeout ms (trigger logout) */
  onIdle: () => void;
  /** Called when activity is detected and timers are reset */
  onActivity?: () => void;
  /** Whether the timer should be active (only when user is authenticated) */
  enabled: boolean;
}

const ACTIVITY_EVENTS: (keyof WindowEventMap)[] = [
  'mousemove',
  'mousedown',
  'keydown',
  'touchstart',
  'scroll',
  'click',
];

/**
 * Tracks user activity and fires onWarning / onIdle callbacks.
 *
 * Timeline:
 *   [activity] → ... (idleTimeout - warningDuration) ms idle ... → onWarning()
 *                → ... warningDuration ms more ... → onIdle()
 *
 * Any activity resets the full timer and cancels any pending warning/idle.
 */
export function useIdleTimer({
  idleTimeout,
  warningDuration,
  onWarning,
  onIdle,
  onActivity,
  enabled,
}: UseIdleTimerOptions) {
  const warningTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onWarningRef = useRef(onWarning);
  const onIdleRef = useRef(onIdle);
  const onActivityRef = useRef(onActivity);
  const lastActivityResetRef = useRef<number>(0);

  // Keep refs current so timers always call the latest callbacks
  useEffect(() => { onWarningRef.current = onWarning; }, [onWarning]);
  useEffect(() => { onIdleRef.current = onIdle; }, [onIdle]);
  useEffect(() => { onActivityRef.current = onActivity; }, [onActivity]);

  const clearTimers = useCallback(() => {
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
  }, []);

  const resetTimer = useCallback(() => {
    clearTimers();
    if (!enabled) return;

    // Clamp warning so it never exceeds half the idle timeout (prevents negative delay)
    const clampedWarning = Math.min(warningDuration, Math.floor(idleTimeout / 2));
    const warningDelay = idleTimeout - clampedWarning;

    warningTimerRef.current = setTimeout(() => {
      onWarningRef.current();
      idleTimerRef.current = setTimeout(() => {
        onIdleRef.current();
      }, clampedWarning);
    }, warningDelay);
  }, [enabled, idleTimeout, warningDuration, clearTimers]);

  useEffect(() => {
    if (!enabled) {
      clearTimers();
      return;
    }

    resetTimer();
    lastActivityResetRef.current = Date.now();

    const handleActivity = () => {
      const now = Date.now();
      // Avoid timeout churn from high-frequency activity events.
      if (now - lastActivityResetRef.current < 250) {
        return;
      }
      lastActivityResetRef.current = now;
      onActivityRef.current?.();
      resetTimer();
    };
    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, handleActivity, { passive: true }));

    return () => {
      clearTimers();
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, handleActivity));
    };
  }, [enabled, resetTimer, clearTimers]);

  return { resetTimer };
}
